import { Images, ImageOff, AlertTriangle, Star, Check, Share2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardHeader, Pill } from '../_primitives';
import { AiSuggestButton } from './AiSuggestButton';

/** Minimum alt-text length before we flag it as "missing"/too short. */
const MIN_ALT_LENGTH = 6;

export interface EditorImageMeta {
  /** Stable identifier for the image within the article. */
  id: string;
  /** Thumbnail/preview source URL. */
  src: string;
  /** Current alt text (editable). */
  alt: string;
  /** Optional caption (editable). */
  caption?: string;
  /** Marks the article hero/featured image. */
  isHero?: boolean;
  /**
   * When true, an empty alt is treated as intentional (a decorative image
   * where alt="" is the correct screen-reader behavior). Stored in the image
   * node attrs / local state — NEVER a backend metadata field.
   */
  decorative?: boolean;
}

export interface ImageMetadataCardProps {
  /** The article's images, in document order. */
  images: EditorImageMeta[];
  /**
   * The current Open Graph image override URL. The row whose src matches gets
   * the "OG image" pill and its button flips to "Clear OG image".
   */
  ogImageSrc?: string | null;
  /** Fired when an inline alt/caption field changes. */
  onImageFieldChange: (id: string, field: 'alt' | 'caption', value: string) => void;
  /** Optional — when provided, the thumbnail becomes a button that scrolls the editor to the image. */
  onJumpToImage?: (id: string) => void;
  /**
   * Optional — toggle the per-image "decorative" flag (alt="" is intentional).
   * When omitted, the toggle renders disabled (read-only state).
   */
  onImageDecorativeToggle?: (id: string, decorative: boolean) => void;
  /** Optional — designate this image as the article hero (exclusive). */
  onSetHero?: (id: string) => void;
  /** Optional — set/clear this image as the Open Graph share image. */
  onSetOgImage?: (id: string) => void;
  /** Content ID for the per-image AI alt-text suggest endpoint. */
  contentId?: string;
  /** Tenant ID for the per-image AI alt-text suggest endpoint. */
  tenantId?: string;
  /** Disable suggest while whole-run generation is in flight. */
  metadataGenerating?: boolean;
}

/** True when the alt text is empty or shorter than MIN_ALT_LENGTH after trimming. */
function isAltWeak(alt: string): boolean {
  return (alt ?? '').trim().length < MIN_ALT_LENGTH;
}

/** True when the user has marked this image decorative AND kept the alt empty. */
function isEffectivelyDecorative(image: EditorImageMeta): boolean {
  return image.decorative === true && (image.alt ?? '').trim() === '';
}

const fieldInputClassName =
  'w-full px-2 py-1 text-[12px] border border-border rounded-md bg-card outline-none transition-shadow focus:border-coral focus:ring-2 focus:ring-coral/20';

const fieldLabelClassName =
  'block text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-0.5';

const rowActionButtonClassName =
  'inline-flex items-center gap-1 px-1.5 py-0.5 text-[10.5px] font-medium rounded-md border border-border bg-card text-muted-foreground hover:bg-warm-bg hover:text-foreground cursor-pointer';

/**
 * Pure presentational rail card listing the article's images (Family D).
 * Each row shows a thumbnail, inline-editable alt + caption fields, a hero
 * badge, and a destructive "Missing alt" badge when the alt is empty/too short.
 * A per-image "Decorative" toggle (frontend-only) suppresses the alarm for
 * intentionally-empty alts (alt="" is the correct screen-reader behavior for
 * decorative images) and shows a calm "Decorative" pill instead.
 * Per-row "Set as hero" / "Use as OG image" buttons let the user designate
 * the hero image (exclusive) and the Open Graph share image explicitly.
 * No fetching, no editor coupling — fully controlled via props.
 */
export function ImageMetadataCard({
  images,
  ogImageSrc,
  onImageFieldChange,
  onJumpToImage,
  onImageDecorativeToggle,
  onSetHero,
  onSetOgImage,
  contentId,
  tenantId,
  metadataGenerating,
}: ImageMetadataCardProps) {
  const count = images.length;
  // "Missing alt" counts only alts that are weak AND not intentionally decorative.
  const missingAlt = images.reduce(
    (n, img) => (isAltWeak(img.alt) && !isEffectivelyDecorative(img) ? n + 1 : n),
    0,
  );
  const summary = `${count} ${count === 1 ? 'image' : 'images'} · ${missingAlt} missing alt`;

  return (
    <Card railSection="images">
      <CardHeader
        icon={<Images className="size-3.5 text-coral" />}
        title="Images"
        trailing={
          count > 0 ? (
            <span className="text-[10.5px] text-muted-foreground">{summary}</span>
          ) : undefined
        }
      />

      <div className="px-3 pb-3">
        {count === 0 ? (
          <div
            className={cn(
              'aspect-video rounded-md px-3.5 py-3.5 text-center',
              'flex flex-col items-center justify-center gap-1',
              'border-[1.5px] border-dashed border-border bg-warm-bg',
            )}
          >
            <ImageOff className="size-5 text-muted-foreground" />
            <div className="text-[12px] font-semibold text-foreground">No images yet</div>
            <div className="text-[10.5px] text-muted-foreground leading-snug">
              Images added to the article will appear here.
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {images.map((image) => {
              const altWeak = isAltWeak(image.alt);
              const decorative = isEffectivelyDecorative(image);
              const showMissingAlt = altWeak && !decorative;
              const isOgImage = Boolean(ogImageSrc) && image.src === ogImageSrc;
              const thumb = (
                <img
                  src={image.src}
                  alt={image.alt || ''}
                  className="size-10 rounded-md object-cover border border-border bg-warm-bg"
                />
              );

              return (
                <div key={image.id} className="flex items-start gap-2.5">
                  <div className="shrink-0">
                    {onJumpToImage ? (
                      <button
                        type="button"
                        onClick={() => onJumpToImage(image.id)}
                        aria-label="Jump to image in article"
                        className="block rounded-md outline-none focus:ring-2 focus:ring-coral/40 cursor-pointer"
                      >
                        {thumb}
                      </button>
                    ) : (
                      thumb
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1 mb-1.5 empty:hidden">
                      {image.isHero && (
                        <Pill tone="coral" size="sm" icon={<Star className="size-3" />}>
                          Hero
                        </Pill>
                      )}
                      {isOgImage && (
                        <Pill tone="success" size="sm" icon={<Share2 className="size-3" />}>
                          OG image
                        </Pill>
                      )}
                      {showMissingAlt && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium rounded-md border bg-destructive/10 text-destructive border-destructive/30">
                          <AlertTriangle className="size-3" />
                          Missing alt
                        </span>
                      )}
                      {decorative && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium rounded-md border bg-warm-bg text-muted-foreground border-border">
                          <Check className="size-3" />
                          Decorative
                        </span>
                      )}
                    </div>

                    <div className="mb-1.5">
                      {/* Not a <label> wrapping the input: nesting the Suggest
                          button inside a <label> pollutes the button's
                          accessible name with the label + input value (the
                          label association algorithm folds in sibling
                          descendant text). Mirrors SocialMetadataCard's
                          label-row-above-the-input pattern instead. */}
                      <span className="inline-flex items-center gap-1.5 mb-0.5">
                        <span className={cn(fieldLabelClassName, 'mb-0')}>Alt text</span>
                        {contentId && tenantId && (
                          <AiSuggestButton
                            field="image_alt"
                            onSuggest={(value) => onImageFieldChange(image.id, 'alt', value)}
                            currentValue={image.alt}
                            contentId={contentId}
                            tenantId={tenantId}
                            disabled={metadataGenerating}
                            extraParams={{ image_src: image.src }}
                            preview
                          />
                        )}
                      </span>
                      <input
                        type="text"
                        value={image.alt}
                        onChange={(e) => onImageFieldChange(image.id, 'alt', e.target.value)}
                        placeholder={
                          decorative
                            ? 'Empty — marked decorative'
                            : 'Describe the image for screen readers'
                        }
                        className={cn(
                          fieldInputClassName,
                          showMissingAlt && 'border-destructive/50 focus:border-destructive',
                        )}
                      />
                    </div>

                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <label className="flex items-center gap-1 text-[10.5px] text-muted-foreground cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={image.decorative === true}
                          onChange={(e) => onImageDecorativeToggle?.(image.id, e.target.checked)}
                          disabled={!onImageDecorativeToggle}
                          className="size-3 accent-coral align-middle"
                          aria-label={`Mark image ${image.id} decorative`}
                        />
                        <span>Decorative (alt empty on purpose)</span>
                      </label>
                    </div>

                    <label className="block">
                      <span className={fieldLabelClassName}>Caption</span>
                      <input
                        type="text"
                        value={image.caption ?? ''}
                        onChange={(e) => onImageFieldChange(image.id, 'caption', e.target.value)}
                        placeholder="Optional caption"
                        className={fieldInputClassName}
                      />
                    </label>

                    {(onSetHero || onSetOgImage) && (
                      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                        {onSetHero && !image.isHero && (
                          <button
                            type="button"
                            onClick={() => onSetHero(image.id)}
                            className={rowActionButtonClassName}
                          >
                            <Star className="size-3" />
                            Set as hero
                          </button>
                        )}
                        {onSetOgImage && (
                          <button
                            type="button"
                            onClick={() => onSetOgImage(image.id)}
                            className={rowActionButtonClassName}
                          >
                            <Share2 className="size-3" />
                            {isOgImage ? 'Clear OG image' : 'Use as OG image'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="text-[10.5px] text-muted-foreground mt-3 border-t border-border pt-2">
          Alt text saves into the article body. WordPress media-library alt is not yet synced
          automatically.
        </p>
      </div>
    </Card>
  );
}
